
import { Vue, Component, Prop } from "vue-property-decorator";
import GET_CONTRACTOR_VISITS from "@/apollo/projects/GetContractorClientsVisits.gql";
import { User, Company, Project } from "@/generated/graphql";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import ClientsVisitsCard from "@/components/companyProjects/ClientsVisitsCard.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import RouteNames from "@/entities/RouteNames";
import { cloneDeep } from "lodash-es";
import { isEqual } from "lodash-es";
import ContractorClientsVisitsFiltersQuery from "@/entities/filters/ContractorClientsVisitsFilters";
import contractorClientsVisitsFiltersTemplate from "@/data/filtersTemplates/contractorClientsVisitsFiltersTemplate";
import ClientsVisitsCollectionFilters from "@/components/companyProjects/ClientsVisitsCollectionFilters.vue";
import ProjectsFiltersQuery from "@/entities/filters/ClientsFiltersQuery";
import InfoCircleFilled from "@/components/icons/InfoCircleFilled.vue";
import Roles from "@/entities/Roles";
import moment from "moment";

@Component({
  components: {
    LoadingOverlay,
    CardPagination,
    ClientsVisitsCard,
    EmptyListPlaceholder,
    ClientsVisitsCollectionFilters,
    EmptyListImageProjects,
    InfoCircleFilled,
  },
  apollo: {
    clients: {
      query: GET_CONTRACTOR_VISITS,
      variables() {
        return this.visitsQueryFilter;
      },
      update(data) {
        const filteredClients = data.clients.filter(
          (client) =>
            client.project_activities && client.project_activities.length > 0
        );
        this.paginationConfig.total = filteredClients.length;
        this.totalCount = filteredClients.length;
        this.paginationConfig.loadedCount = data.clients.length;
        return filteredClients;
      },
      skip() {
        return !this.currentUser?.id;
      },
    },
  },
})
export default class OfferedVisits extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;

  clients: Project[] | null = null;
  paginationConfig: PaginationConfig = { ...paginationConfig };
  routeNames = RouteNames;
  totalCount: number = 0;

  get loading(): boolean {
    return this.$apollo.queries.clients.loading;
  }
  projectFilters: ContractorClientsVisitsFiltersQuery = cloneDeep(
    contractorClientsVisitsFiltersTemplate
  );
  resetFilters(): void {
    if (isEqual(this.projectFilters, contractorClientsVisitsFiltersTemplate)) {
      return;
    }

    this.projectFilters = cloneDeep(contractorClientsVisitsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  get emptyListMessage(): string {
    // const entitiesName = "clients";
    // const filtersEmpty = isEqual(
    //   this.projectFilters,
    //   contractorClientsVisitsFiltersTemplate
    // );

    // if (filtersEmpty) {
    //   return getEmptyListMessage(entitiesName);
    // }

    // return getEmptyFiltersResultMessage(entitiesName);
    return "You currently do not have any Visits";
  }

  getCheckInMessage(): string {
    if (this.currentUser?.role === Roles.CONTRACTOR) {
      return "Check-ins open 10 minutes before the visit. If not completed by 5 minutes before, it will be marked late. Contact your manager if you have any issues.";
    }
    return "Visit check-ins open 10 minutes before the scheduled start time and are considered late if not completed within 5 minutes after that time.";
  }

  get visitsQueryFilter() {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;
    const { client_name, managers, location, contractorUpdatedAt } =
      this.projectFilters;
    const filterConditions = [];
    if (managers.length) {
      filterConditions.push({
        manager: {
          id: { _in: managers },
        },
      });
    }
    if (client_name) {
      filterConditions.push({
        client_name: { _ilike: `%${client_name}%` },
      });
    }
    if (location) {
      filterConditions.push({
        project_activities: { location: { _ilike: `%${location}%` } },
      });
    }

    if (contractorUpdatedAt) {
      const startDate = moment(contractorUpdatedAt)
        .utc()
        .startOf("day")
        .toISOString();
      const endDate = moment(contractorUpdatedAt)
        .utc()
        .endOf("day")
        .toISOString();

      const datesConditions = [
        {
          project_activities: {
            _and: [
              { contractor_updated_at: { _gte: startDate } },
              { contractor_updated_at: { _lte: endDate } },
            ],
          },
        },
      ];

      filterConditions.push({
        _and: datesConditions,
      });
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: [{ drafted: { _eq: false } }, ...filterConditions],
      },
      projectActivitiesWhere: {
        _and: [
          {
            _or: [
              { accepted: { _is_null: true } },
              // { accepted: { _eq: false } }
            ],
          },
          {
            _or: [
              { cancelled: { _is_null: true } },
              { cancelled: { _eq: false } },
            ],
          },
          { deleted: { _eq: false } },
          { drafted: { _eq: false } },
          { completed: { _eq: false } },
          {
            contractor: {
              user: {
                id: { _eq: this.currentUser.id },
              },
            },
          },
        ],
      },
    };
  }

  updateFilters(filters: ProjectsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.projectFilters);

    if (dataEqual) {
      return;
    }
    this.projectFilters = { ...filters, title: "" };
  }

  get empty(): boolean {
    if (!this.clients) {
      return false;
    }
    return this.clients?.length === 0;
  }
}
